// libs
import { AudioPreview } from '@lopo-tech/frontends-shared/lib/sharedComponents/AudioPreview/AudioPreview'
import { VideoPreview } from '@lopo-tech/frontends-shared/lib/sharedComponents/VideoPreview/VideoPreview'
import classNames from 'classnames'
import { History } from 'history'
import * as React from 'react'
// interfaces / constants
import { THUMBNAIL_SIZE_TYPE_XLARGE } from 'src/components/thumbnail/thumbnail'
import {
  FOLLOWERS_PARTIAL,
  FRONTEND_EDIT_ABOUT_ADD_AUDIO_LINK,
  FRONTEND_EDIT_ABOUT_ADD_VIDEO_LINK,
  FRONTEND_EDIT_DESCRIPTION_PATH,
  FRONTEND_EDIT_IMPRESSUM_PATH,
  FRONTEND_EDIT_INTERNET_SITE_PATH,
  FRONTEND_EDIT_TAGS_PATH,
  FRONTEND_SETTINGS_PATH
} from 'src/constants/urls'
import { ILocationShape } from 'src/interfaces/location'
import { COLOR_TYPE_GRAY_TINT_9, COLOR_TYPE_PRIMARY } from 'src/utils/color'

// classes
import Avatar from 'src/components/avatar/avatar'
import LegacyButton, {
  BUTTON_TYPE_CONTAINED
} from 'src/components/forms/button/button'
import Icon from 'src/components/forms/icon/icon'
import PreviewWithConsent from 'src/components/gdpr/preview_with_consent/preview_with_consent'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import ProfileTag from 'src/components/tag/profile-tag'
import FollowButton from 'src/containers/smart_components/follow_button/follow_button'
import UserOnboardingCTA from 'src/containers/smart_components/user_onboarding_cta/user_onboarding_cta'

// helpers
import Translate, { textResources } from 'src/lang/de'
import { getSponsorBadge } from 'src/utils/badges'
import { createBemBlock } from 'src/utils/bem_helper/bem_helper'
import { getConfigValue } from 'src/utils/config/config'
import { isSafeTarget } from 'src/utils/safe_url/safe_url'
import { makeHTMLLinksFromText } from 'src/utils/string/string'
import { UrlUtils } from 'src/utils/url/url'

import {
  ActorProfileDetailFragmentFragment,
  ProfileLinkPurpose,
  ProfileLinksFragmentFragment,
  ShapeSponsorshipLevel
} from '@lopo-tech/lopo-api'
import 'src/components/profile/profile_info.scss'
import {
  isMessengerFlagEnabled
} from '../settings/settings_wizard/steps/settings_overview/messenger_toggle/helpers'
import RemoveLink from './remove_link/remove_link'

type AudioLink = ProfileLinksFragmentFragment['items'][number] & { purpose: ProfileLinkPurpose.SpotifySong }
type VideoLink = ProfileLinksFragmentFragment['items'][number] & { purpose: ProfileLinkPurpose.YouTubeVideo }

interface IProps {
  locationShapeId?: string;
  isCurrentProfile: boolean;
  changePosition: (locationShape: ILocationShape) => void;
  profile: ActorProfileDetailFragmentFragment;
  history: History;
  isMessengerEnabled: boolean;
}

interface IState {
  showImprint: boolean;
  showMenu: boolean;
  showAudioLink: boolean;
  showVideoLink: boolean;
}

const cls = createBemBlock('profile-info')
const labels = textResources.profile
const sharedLabels = textResources.shared

interface IOptionalPressLabel {
  // `unknown` for the second arm on purpose: a fork can write this key as a string, and a
  // type that rules that out would make the check below look pointless to the next reader.
  pressProfileLabel?: ((location?: string) => string) | unknown;
}

export const getProfileLabel = (
  profile: ActorProfileDetailFragmentFragment,
  locationShapeName: string | undefined,
  showLocation: boolean | undefined
): string | null => {
  if (profile.shapeSponsorshipLevel) {
    switch (profile.shapeSponsorshipLevel) {
      case ShapeSponsorshipLevel.Bronze:
        return textResources.shapeSponsors.bronzePartner
      case ShapeSponsorshipLevel.Silver:
        return textResources.shapeSponsors.silverPartner
    }
  }

  if (profile.__typename === 'AuthorityProfile') {
    return labels.authorityProfile
  }

  return getLabelWithLocation(
    profile.__typename,
    locationShapeName,
    showLocation
  )
}

const getLabelWithLocation = (
  profileType: ActorProfileDetailFragmentFragment['__typename'],
  locationShapeName?: string,
  showLocation?: boolean
) => {
  switch (profileType) {
    case 'PrivateProfile':
      return showLocation
        ? `${labels.neighborFromMale} ${locationShapeName}`
        : labels.neighborMale
    case 'PressProfile': {
      // An instance owns this line through `pressProfileLabel`, which returns it whole
      // and so can leave the place out. The catalogue an instance installs is not the one
      // this type describes: fork language files replace the base file as a whole, so the
      // key can be missing, and it can be there with the wrong shape. A fork that writes
      // it as a plain string type checks everywhere and would throw here, which the only
      // error boundary of this app turns into a blank page, so the check asks whether the
      // value can be called rather than whether it is set. Either way the two older keys
      // carry the line, which is the wording such a fork already has.
      const { pressProfileLabel } = labels as IOptionalPressLabel

      return typeof pressProfileLabel === 'function'
        ? pressProfileLabel(showLocation ? locationShapeName : undefined)
        : showLocation
          ? `${labels.pressProfile} ${locationShapeName}`
          : labels.press
    }
    case 'BlogProfile':
      return showLocation
        ? `${labels.blogProfile} ${locationShapeName}`
        : labels.blog
    default:
      return Translate.authorType[profileType]
  }
}

class ProfileInfo extends React.PureComponent<IProps, IState> {
  constructor (props: IProps) {
    super(props)

    this.state = {
      showAudioLink: true,
      showImprint: false,
      showMenu: false,
      showVideoLink: true
    }

    this.onMessage = this.onMessage.bind(this)
    this.toggleImprint = this.toggleImprint.bind(this)
    this.renderEditIcon = this.renderEditIcon.bind(this)
    this.renderVerifiedIcon = this.renderVerifiedIcon.bind(this)
  }

  public componentDidMount () {
    this.setAppLocation()
  }

  public componentDidUpdate () {
    this.setAppLocation()
  }

  public render () {
    const { profile } = this.props
    const { avatar, name, address, shapeSponsorshipLevel } = profile

    return (
      <div className={cls()}>
        <div className={cls('tile')}>
          <div className={cls('header')}>
            <Avatar
              rounded={false}
              curved={true}
              image={avatar.previewUrl}
              size={THUMBNAIL_SIZE_TYPE_XLARGE}
              badge={
                shapeSponsorshipLevel && getSponsorBadge(shapeSponsorshipLevel)
              }
            />
            <div className={cls('title')}>
              <h1 className={cls('name')}>{name}</h1>
              {address.locationShape.slug && this.renderDescription()}
            </div>
          </div>
          {this.renderContent()}
          {this.renderPublicInfo()}
          {this.renderImprint()}
          {this.renderTags()}
          {/* TODO enable when backend is fixed {this.renderMyGroups()} */}
          {this.renderLinks()}
          {this.renderToolbar()}
        </div>
      </div>
    )
  }

  private renderLinks () {
    const audioLink = this.props.profile.profileLinks.items.find(
      (item): item is AudioLink =>
        item.purpose === ProfileLinkPurpose.SpotifySong
    )
    const videoLink = this.props.profile.profileLinks.items.find(
      (item): item is VideoLink =>
        item.purpose === ProfileLinkPurpose.YouTubeVideo
    )

    if (getConfigValue('featureFlags.factSheet')) {
      return (
        <>
          {this.renderAudioLink(audioLink)}
          {this.renderVideoLink(videoLink)}
        </>
      )
    }
    return null
  }

  // Helper to detect audio service from URL
  private getAudioSource (url?: string): 'spotify' | 'apple_music' | 'audio' {
    if (!url) return 'audio'
    if (url.includes('spotify.com') || url.includes('spotify:')) return 'spotify'
    if (url.includes('apple.com') || url.includes('music.apple')) return 'apple_music'
    return 'audio' // fallback for unknown services
  }

  private renderAudioLink (audioLink?: AudioLink) {
    const { isCurrentProfile } = this.props
    const { showAudioLink } = this.state

    if (!audioLink && !isCurrentProfile) {
      return null
    }

    const onDelete = () => {
      this.setState({
        showAudioLink: false
      })
    }

    const audioSource = this.getAudioSource(audioLink?.url)

    return (
      <div className={classNames(cls('content'), cls('content', 'no-border'))}>
        <div>
          <span className={cls('content-title')}>
            {isCurrentProfile
              ? textResources.onboardingSettings.addAudioLink
              : textResources.onboardingSettings.myAudioLink}
          </span>
          {isCurrentProfile &&
            this.renderEditIcon(FRONTEND_EDIT_ABOUT_ADD_AUDIO_LINK)}
        </div>
        {showAudioLink && (
          <>
            <div className={cls('profile-links')}>
              <PreviewWithConsent type="audio" source={audioSource}>
                <AudioPreview url={audioLink?.url} />
              </PreviewWithConsent>
            </div>
            {isCurrentProfile && (
              <RemoveLink
                linkId={audioLink?.id}
                onDelete={onDelete}
                size={12}
              />
            )}
          </>
        )}
      </div>
    )
  }

  private renderVideoLink (videoLink?: VideoLink) {
    const { isCurrentProfile } = this.props
    const { showVideoLink } = this.state

    if (!videoLink?.url && !isCurrentProfile) {
      return null
    }

    const onDelete = () => {
      this.setState({
        showVideoLink: false
      })
    }

    return (
      <div className={classNames(cls('content'), cls('content', 'no-border'))}>
        <div>
          <span className={cls('content-title')}>
            {isCurrentProfile
              ? textResources.onboardingSettings.addVideoLink
              : textResources.onboardingSettings.myVideoLink}
          </span>
          {isCurrentProfile &&
            this.renderEditIcon(FRONTEND_EDIT_ABOUT_ADD_VIDEO_LINK)}
        </div>
        {showVideoLink && (
          <>
            <div className={cls('profile-links')}>
              <PreviewWithConsent type="video" source="youtube">
                <VideoPreview url={videoLink?.url} />
              </PreviewWithConsent>
            </div>
            {isCurrentProfile && (
              <RemoveLink
                linkId={videoLink?.id}
                onDelete={onDelete}
                size={12}
              />
            )}
          </>
        )}
      </div>
    )
  }

  private renderToolbar (): JSX.Element | null {
    const { isCurrentProfile, profile } = this.props

    // for the right aligned design we styled this in reversed order from right to left
    if (isCurrentProfile && profile.canUpdate.value) {
      return this.renderToolbarWithContent(
        this.renderOwnProfilesToolbar(profile, isCurrentProfile)
      )
    }

    return this.renderOtherProfilesToolbar(profile)
  }

  private renderToolbarWithContent (content: JSX.Element | null) {
    return <div className={cls('toolbar')}>{content}</div>
  }

  private renderOwnProfilesToolbar (
    profile: ActorProfileDetailFragmentFragment,
    isCurrentProfile: boolean
  ): JSX.Element | null {
    const { canUpdate, canShowFollowersList } = profile

    return (
      <>
        {isCurrentProfile && canUpdate.value && (
          <NavigationItem
            target={'_self'}
            url={FRONTEND_SETTINGS_PATH}
            className={cls('edit-button')}
          >
            {textResources.profile.editProfile}
          </NavigationItem>
        )}
        {canShowFollowersList.value && (
          <NavigationItem
            target={'_self'}
            url={
              UrlUtils.getProfileFrontendPath(
                profile.slug,
                profile.__typename
              ) + FOLLOWERS_PARTIAL
            }
          >
            {textResources.follow.showFollowers}
          </NavigationItem>
        )}
      </>
    )
  }

  private renderOtherProfilesToolbar (
    profile: ActorProfileDetailFragmentFragment
  ) {
    const { canMessage, canFollow } = profile

    const enableMessenger =
      canMessage && this.props.isMessengerEnabled && isMessengerFlagEnabled()

    if (canFollow) {
      return this.renderToolbarWithContent(
        <>
          {enableMessenger && (
            <UserOnboardingCTA active={!canMessage}>
              <NavigationItem target={'_self'} onClick={this.onMessage}>
                {sharedLabels.writeMessage}
              </NavigationItem>
            </UserOnboardingCTA>
          )}
          <FollowButton actorProfile={profile} />
        </>
      )
    }

    if (enableMessenger) {
      return this.renderToolbarWithContent(
        <UserOnboardingCTA
          active={!canMessage}
          className={cls('feature-blocker')}
        >
          <LegacyButton
            onClick={this.onMessage}
            label={sharedLabels.sayHello}
            variant={BUTTON_TYPE_CONTAINED}
            fullWidth
            color={COLOR_TYPE_GRAY_TINT_9}
            lowerCase
            primary={true}
          />
        </UserOnboardingCTA>
      )
    }

    return null
  }

  private renderContent (): JSX.Element | null {
    const {
      profile: { description, canUpdate },
      isCurrentProfile
    } = this.props

    if (!description) {
      return null
    }
    const message =
      makeHTMLLinksFromText(description) ||
      textResources.onboardingSettings.profileDescriptionPlaceholder

    return (
      <div className={cls('content')}>
        <p className={cls('content-title')}>{labels.aboutMe}</p>
        {isCurrentProfile && canUpdate.value
          ? (
            <p>
              <span
                className={cls('content-body')}
                dangerouslySetInnerHTML={{ __html: message }}
              />
              {this.renderEditIcon(FRONTEND_EDIT_DESCRIPTION_PATH)}
            </p>
          )
          : (
            <p
              className={cls('content-body')}
              dangerouslySetInnerHTML={{ __html: message }}
            />
          )}
      </div>
    )
  }

  private renderDescription (): JSX.Element | null {
    const { profile } = this.props

    const label = getProfileLabel(
      profile,
      profile.address.locationShape.name,
      getConfigValue('featureFlags.map.enabled')
    )
    const verifiedIcon = this.renderVerifiedIcon()

    if (!label && !verifiedIcon) {
      return null
    }

    return (
      <p className={cls('description')}>
        {label}
        {verifiedIcon}
      </p>
    )
  }

  private renderVerifiedIcon (): JSX.Element | null {
    if (this.props.profile.__typename !== 'AuthorityProfile') {
      return null
    }

    return (
      <Icon
        className={cls('icon-verified')}
        name={'checkmark'}
        size={14}
        description={labels.authorityProfile}
        color={COLOR_TYPE_PRIMARY}
      />
    )
  }

  private renderEditIcon (url: string): JSX.Element | null {
    if (!(this.props.profile.canUpdate.value && this.props.isCurrentProfile)) {
      return null
    }
    return (
      <NavigationItem url={url} target="_self" className={cls('icon')}>
        <Icon
          className={cls('icon', 'right')}
          name={'pencil'}
          size={12}
          description={sharedLabels.edit}
        />
      </NavigationItem>
    )
  }

  private renderPublicInfo (): JSX.Element | null {
    const { profile, isCurrentProfile } = this.props
    if (profile.__typename === 'PrivateProfile') {
      return null
    }

    const clsActive = {
      [cls('item', 'active')]: this.state.showImprint
    }

    const showUpdateOption = isCurrentProfile && profile.canUpdate.value

    // The profile's own website, written by whoever owns the profile. It is
    // checked here and not in the handful of sibling components that render a
    // target the same way, because this file already renders another value that
    // needs the same care and treating two targets in one file differently
    // would not be explainable to a reader. The siblings are their own change.
    // Without a target NavigationItem renders a plain element, so a website we
    // cannot link is still shown as text - only the link goes.
    const website = isSafeTarget(profile.website) ? profile.website : undefined

    return (
      <ul className={cls('public')}>
        {(profile.website || showUpdateOption) && (
          <li className={cls('item')}>
            <NavigationItem
              url={website}
              target={'_blank'}
              className={cls('navigation')}
            >
              <Icon
                name={'world'}
                className={cls('icon', 'left')}
                size={16}
                color={COLOR_TYPE_GRAY_TINT_9}
              />
              <span>{profile.website || 'Webseite'}</span>
            </NavigationItem>
            {this.renderEditIcon(FRONTEND_EDIT_INTERNET_SITE_PATH)}
          </li>
        )}
        {(profile.siteNotice || showUpdateOption) && (
          <li className={classNames(cls('item'), clsActive)}>
            <button
              onClick={this.toggleImprint}
              disabled={!profile.siteNotice}
              className={cls('navigation')}
            >
              <Icon
                name={'imprint'}
                className={cls('icon', 'left')}
                size={16}
                color={COLOR_TYPE_GRAY_TINT_9}
              />
              {sharedLabels.imprint}
            </button>
            {this.renderEditIcon(FRONTEND_EDIT_IMPRESSUM_PATH)}
          </li>
        )}
      </ul>
    )
  }

  private renderImprint (): JSX.Element | null {
    const { profile } = this.props
    const siteNotice =
      profile.__typename !== 'PrivateProfile' ? profile.siteNotice : undefined

    if (!this.state.showImprint || !siteNotice) {
      return null
    }

    const siteNoticeWithClickableLinks = makeHTMLLinksFromText(siteNotice)

    return (
      <div className={cls('imprint')}>
        <p dangerouslySetInnerHTML={{ __html: siteNoticeWithClickableLinks }} />
      </div>
    )
  }

  private renderTags (): JSX.Element | null {
    const {
      profile: { tags, canUpdate },
      isCurrentProfile
    } = this.props

    const showUpdateOption = canUpdate.value && isCurrentProfile

    if (!tags || (tags.length === 0 && !showUpdateOption)) {
      return null
    }

    return (
      <div className={classNames(cls('content'), cls('content', 'no-border'))}>
        <p className={cls('content-title')}>{labels.myInterests}</p>
        {tags.length > 0 && (
          <div className={cls('tags')}>
            {tags.map((tag) => (
              <ProfileTag
                text={tag.name}
                key={tag.name}
              />
            ))}
          </div>
        )}
        {tags.length === 0 && showUpdateOption && (
          <p className={cls('add-tags-cta')}>{labels.giveYourInterests}</p>
        )}
        {showUpdateOption && (
          <NavigationItem url={FRONTEND_EDIT_TAGS_PATH}>
            <Icon
              name="plus-circle"
              description={sharedLabels.addInterests}
              size={20}
              color={COLOR_TYPE_GRAY_TINT_9}
              className={cls('add-tag-icon')}
            />
          </NavigationItem>
        )}
      </div>
    )
  }

  // private renderMyGroups() {
  //   if (getConfigValue('featureFlags.factSheet')) {
  //     const { profile: { member_of } } = this.props;
  //     if (!member_of || member_of.length === 0) {
  //       return null;
  //     }
  //
  //     return (
  //       <div className={classNames(cls('content'), cls('content', 'no-border'))}>
  //         <p className={cls('content-title')}>{labels.myGroups}</p>
  //         <div className={cls('tags')}>
  //           {member_of.map(tag => (
  //             <Tag
  //               url={UrlUtils.groupPage(tag.slug)}
  //               text={tag.name}
  //               key={tag.slug}
  //               className={cls('tag')} isClickable
  //             />))
  //           }
  //         </div>
  //       </div>);
  //   }
  //
  //   return null;
  // }

  private toggleImprint () {
    this.setState({
      showImprint: !this.state.showImprint
    })
  }

  private onMessage () {
    const { profile, history } = this.props

    history.push(
      UrlUtils.sendMessageConversationPage(profile.__typename, profile.id)
    )
  }

  private setAppLocation () {
    const { changePosition, profile, locationShapeId } = this.props
    // only set the app-position to the seen profile in deeplinking when no position from other sources
    if (!locationShapeId && profile.address.locationShape) {
      changePosition(profile.address.locationShape)
    }
  }
}

export default ProfileInfo
